// EmailVerificationScreen Component
// Shows verification pending screen for email/password users

import React, { useState, useEffect } from 'react';
import { auth } from '../../firebaseConfig';
import { sendEmailVerification } from 'firebase/auth';
import { FaEnvelope, FaCheckCircle, FaRedo, FaSignOutAlt } from 'react-icons/fa';
import styles from './EmailVerificationScreen.module.css';

const EmailVerificationScreen = () => {
  const [checking, setChecking] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const user = auth.currentUser;

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Auto-check verification every 5 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      if (user && !checking) {
        try {
          await user.reload();
          if (user.emailVerified) {
            setShowSuccess(true);
            setTimeout(() => {
              window.location.reload(); // Refresh to trigger onboarding
            }, 2000);
          }
        } catch (error) {
          console.error('Error auto-checking verification:', error);
        }
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [user, checking]);

  const handleCheckVerification = async () => {
    setChecking(true);
    try {
      await user.reload();
      
      if (user.emailVerified) {
        setShowSuccess(true);
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        alert('Email not verified yet. Please check your inbox and click the verification link.');
      }
    } catch (error) {
      console.error('Error checking verification:', error);
      alert('Error checking verification status. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const handleResendEmail = async () => {
    if (resendCooldown > 0) return;
    
    try {
      await sendEmailVerification(user);
      setResendCooldown(60); // 60 second cooldown
      alert('Verification email sent! Please check your inbox.');
    } catch (error) {
      console.error('Error resending email:', error);
      if (error.code === 'auth/too-many-requests') {
        alert('Too many requests. Please wait a few minutes before trying again.');
      } else {
        alert('Failed to resend email. Please try again later.');
      }
    }
  };

  const handleSignOut = async () => {
    try {
      await auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (showSuccess) {
    return (
      <div className={styles.container}>
        <div className={styles.successCard}>
          <div className={styles.successIcon}>
            <FaCheckCircle />
          </div>
          <h1>Email Verified!</h1>
          <p>Redirecting you to complete your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {/* Animated Email Icon */}
        <div className={styles.iconWrapper}>
          <div className={styles.emailIcon}>
            <FaEnvelope />
          </div>
          <div className={styles.iconGlow}></div>
        </div>

        {/* Header */}
        <h1 className={styles.title}>Verify Your Email Address</h1>
        
        {/* Email Display */}
        <div className={styles.emailDisplay}>
          <p className={styles.subtitle}>We've sent a verification link to:</p>
          <div className={styles.emailBadge}>
            <FaEnvelope className={styles.badgeIcon} />
            {user?.email}
          </div>
        </div>

        {/* Instructions */}
        <p className={styles.instructions}>
          Please check your inbox and click the verification link to activate your account 
          and start building your player profile.
        </p>

        {/* Primary Action */}
        <button 
          className={styles.primaryButton}
          onClick={handleCheckVerification}
          disabled={checking}
        >
          {checking ? (
            <>
              <span className={styles.spinner}></span>
              Checking...
            </>
          ) : (
            <>
              <FaCheckCircle />
              I've Verified - Let Me In
            </>
          )}
        </button>

        {/* Secondary Actions */}
        <div className={styles.secondaryActions}>
          <button 
            className={styles.secondaryButton}
            onClick={handleResendEmail}
            disabled={resendCooldown > 0}
          >
            <FaRedo />
            {resendCooldown > 0 
              ? `Resend in ${resendCooldown}s` 
              : 'Resend Verification Email'
            }
          </button>

          <button 
            className={styles.tertiaryButton}
            onClick={handleSignOut}
          >
            <FaSignOutAlt />
            Use Different Email
          </button>
        </div>

        {/* Help Section */}
        <div className={styles.helpSection}>
          <p className={styles.helpTitle}>Didn't receive the email?</p>
          <ul className={styles.helpList}>
            <li>Check your spam or junk folder</li>
            <li>Wait a few minutes for the email to arrive</li>
            <li>Make sure <strong>{user?.email}</strong> is correct</li>
            <li>Check your email provider isn't blocking emails</li>
          </ul>
        </div>

        {/* Auto-check indicator */}
        <div className={styles.autoCheckNote}>
          <div className={styles.pulsingDot}></div>
          Automatically checking verification status...
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationScreen;















